import { useState } from 'react';
import { toast } from 'sonner';
import { getAnamnesisApiErrorMessage } from '@/api/patient-anamnesis';
import { Alert } from '@/components/ui/alert';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useUploadAnamnesis } from '@/hooks/use-patient-anamnesis';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';

type Props = {
    patientId: string;
    onUploaded: () => void;
};

const VALID_EXTENSIONS = ['.docx', '.txt', '.pdf'];

/**
 * The "Enviar arquivo" view of the anamnesis start step: one dropzone, the text
 * is extracted server-side on confirm, so success means the anamnesis already
 * exists — the caller just opens the editor.
 */
export default function AnamnesisUploadStep({ patientId, onUploaded }: Props) {
    const [uploadError, setUploadError] = useState<string | null>(null);
    const uploadMutation = useUploadAnamnesis(patientId);
    const anamnesisAccess = useFeatureEntitlement('anamnesis.process');

    const handleFiles = async (files: File[]) => {
        const file = files[0];
        if (!file) return;

        if (anamnesisAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(anamnesisAccess.decision));
            return;
        }

        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!VALID_EXTENSIONS.includes(fileExtension)) {
            toast.error('Por favor, envie um arquivo .docx, .txt ou .pdf');
            return;
        }

        setUploadError(null);
        try {
            await uploadMutation.mutateAsync(file);
            toast.success('Arquivo enviado com sucesso');
            onUploaded();
        } catch (error) {
            setUploadError(
                getAnamnesisApiErrorMessage(error) ?? 'Falha ao enviar arquivo. Tente novamente.',
            );
        }
    };

    return (
        <div className="space-y-3">
            {anamnesisAccess.shouldBlock && (
                <Alert variant="warning" className="text-xs">
                    {getEntitlementBlockedMessage(anamnesisAccess.decision)}
                </Alert>
            )}

            <FileDropzone
                title="Clique para enviar a anamnese"
                description="Arraste ou selecione um arquivo .docx, .txt ou .pdf."
                selectLabel="Selecionar arquivo"
                accept=".docx,.txt,.pdf"
                loading={uploadMutation.isPending}
                disabled={anamnesisAccess.shouldBlock}
                showSelectButton={false}
                onFilesSelected={(files) => void handleFiles(files)}
            />

            {uploadError && <Alert variant="destructive">{uploadError}</Alert>}

            <p className="text-xs text-muted-foreground">
                O texto do arquivo é extraído automaticamente e fica disponível para leitura e
                edição.
            </p>
        </div>
    );
}
